import { useCallback, useEffect, useRef, useState } from 'react';
import { useLang } from '../i18n';

// teachers: [{ name, photo, bio }]
export default function TeacherCarousel({ teachers }) {
  const { t } = useLang();
  const trackRef = useRef(null);
  const [state, setState] = useState({ index: 0, atStart: true, atEnd: false });

  const update = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.firstElementChild;
    const step = card ? card.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 0) : track.clientWidth;
    const maxScroll = track.scrollWidth - track.clientWidth;
    setState({
      index: Math.min(teachers.length - 1, Math.round(track.scrollLeft / step)),
      atStart: track.scrollLeft <= 4,
      atEnd: track.scrollLeft >= maxScroll - 4,
    });
  }, [teachers.length]);

  useEffect(() => {
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [update]);

  const scrollToIndex = (i) => {
    const track = trackRef.current;
    const card = track?.children[i];
    if (card) track.scrollTo({ left: card.offsetLeft - track.offsetLeft, behavior: 'smooth' });
  };

  const move = (dir) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.firstElementChild;
    const step = card.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 0);
    track.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  const onKeyDown = (event) => {
    if (event.key === 'ArrowRight') { event.preventDefault(); move(1); }
    if (event.key === 'ArrowLeft') { event.preventDefault(); move(-1); }
  };

  return (
    <div className="teacher-carousel" role="region" aria-roledescription="carousel" aria-label={t('Equipo de profesores', 'Equip de professors')}>
      <div className="teacher-track" ref={trackRef} onScroll={update} onKeyDown={onKeyDown} tabIndex={0}>
        {teachers.map(({ name, photo, bio }, i) => (
          <article className="teacher-card" key={name} role="group" aria-roledescription="slide" aria-label={`${i + 1} / ${teachers.length}`}>
            <img src={photo} alt={t(`${name}, profesora o profesor de Speak English Now`, `${name}, professora o professor de Speak English Now`)} loading="lazy" draggable="false" />
            <div><h3>{name}</h3><p>{bio}</p></div>
          </article>
        ))}
      </div>
      <div className="teacher-controls">
        <button type="button" className="teacher-arrow" onClick={() => move(-1)} disabled={state.atStart} aria-label={t('Profesor anterior', 'Professor anterior')}>←</button>
        <div className="teacher-dots">
          {teachers.map(({ name }, i) => (
            <button key={name} type="button" className={i === state.index ? 'is-active' : ''} onClick={() => scrollToIndex(i)} aria-label={name} aria-current={i === state.index ? 'true' : undefined} />
          ))}
        </div>
        <button type="button" className="teacher-arrow" onClick={() => move(1)} disabled={state.atEnd} aria-label={t('Profesor siguiente', 'Professor següent')}>→</button>
      </div>
    </div>
  );
}
